import React from 'react';
import { Activity, CheckCircle2, AlertTriangle, AlertCircle } from 'lucide-react';
import { useConsoleTheme } from '../../lib/theme';

const STATUS = {
  online: {
    color: '#B0FC38',
    label: 'Online',
    icon: CheckCircle2,
    hint: 'All systems operational',
  },
  degraded: {
    color: '#facc15',
    label: 'Minor errors',
    icon: AlertTriangle,
    hint: 'A small share of requests had errors, we keep an eye on this',
  },
  offline: {
    color: '#ef4444',
    label: 'Investigating',
    icon: AlertCircle,
    hint: 'Higher error rate than usual, our team is looking into it',
  },
  unknown: {
    color: '#6b7280',
    label: 'Monitoring',
    icon: Activity,
    hint: 'Not enough traffic yet for a reliable reading',
  },
};

// Light theme (docs/light-mode.md §3): the same status colours as readable
// text on paper — accent text, the status hues at 800/700 (the generator's
// choice for status text), ink-3 for "monitoring".
const LIGHT_COLOR = {
  online: '#41680D',
  degraded: '#854D0E',
  offline: '#B91C1C',
  unknown: '#5C584C',
};

export default function UptimeIndicator({ status, uptime, range, loading }) {
  const { isLight } = useConsoleTheme();
  if (loading) {
    return (
      <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06] h-full">
        <div className="h-20 rounded bg-white/[0.03] animate-pulse" />
      </div>
    );
  }

  const key = STATUS[status] ? status : 'unknown';
  const cfg = isLight ? { ...STATUS[key], color: LIGHT_COLOR[key] } : STATUS[key];
  const Icon = cfg.icon;

  return (
    <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06] h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] font-medium text-gray-500 tracking-wider uppercase">Uptime</div>
        <Activity className="w-4 h-4 text-gray-600" />
      </div>

      <div className="flex items-baseline gap-1.5">
        <div className="text-[1.75rem] leading-none font-semibold text-white tracking-tight tabular-nums">
          {uptime == null ? '—' : uptime.toFixed(2)}
        </div>
        <div className="text-sm text-gray-500 font-medium">%</div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <span className="relative inline-flex w-2 h-2">
          <span
            className="absolute inset-0 rounded-full animate-ping opacity-60"
            style={{ backgroundColor: cfg.color }}
          />
          <span
            className="relative inline-flex w-2 h-2 rounded-full"
            style={{ backgroundColor: cfg.color }}
          />
        </span>
        <span className="inline-flex items-center gap-1.5 text-[12px] font-medium" style={{ color: cfg.color }}>
          <Icon className="w-3 h-3" />
          {cfg.label}
        </span>
      </div>

      <div className="mt-auto pt-3 text-[11px] text-gray-500 leading-relaxed">
        {cfg.hint}
        {range ? ` · last ${range} days` : ''}
      </div>
    </div>
  );
}
